import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  ArrowLeft,
  Wallet,
  Plus,
  Loader2,
  TrendingUp,
  TrendingDown,
  Coins,
  Users,
  User,
  Sparkles,
  CreditCard,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle,
  Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";

const WalletPage = ({ user }) => {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [allWallets, setAllWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [viewMode, setViewMode] = useState("personal");
  
  const canManageWallet = user?.role === "admin" || user?.role === "titolare";
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const requests = [axios.get(`${API}/wallet`, { withCredentials: true })];
      
      if (canManageWallet) {
        requests.push(axios.get(`${API}/wallet/all`, { withCredentials: true }));
      }
      
      const responses = await Promise.all(requests);
      setWallet(responses[0].data);
      
      if (canManageWallet && responses[1]) {
        setAllWallets(responses[1].data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento portafoglio");
    } finally {
      setLoading(false);
    }
  };
  
  const handleAddCredits = async () => {
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }
    
    if (!selectedUserId) {
      toast.error("Seleziona un collaboratore");
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API}/wallet/add/${selectedUserId}`, {
        amount: numAmount,
        description: description || "Ricarica Ortho"
      }, { withCredentials: true });
      
      const selectedUser = allWallets.find(w => w.user_id === selectedUserId);
      toast.success(`Aggiunti ${numAmount} Ortho a ${selectedUser?.name || 'utente'}`);
      setAddDialogOpen(false);
      setAmount("");
      setDescription("");
      setSelectedUserId("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'aggiunta crediti");
    } finally {
      setProcessing(false);
    }
  };
  
  const handleSettleWallet = async (targetUserId, userName, currentBalance) => {
    if (!confirm(`Confermi di voler saldare ${currentBalance.toFixed(2)} Ortho a ${userName}? Il saldo tornerà a zero.`)) {
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API}/wallet/settle/${targetUserId}`, {}, { withCredentials: true });
      toast.success(`Saldo di ${userName} saldato (${currentBalance.toFixed(2)} Ortho)`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel saldo");
    } finally {
      setProcessing(false);
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };
  
  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-100 text-purple-700 border-purple-200",
      titolare: "bg-amber-100 text-amber-700 border-amber-200",
      collaboratore: "bg-slate-100 text-slate-700 border-slate-200"
    };
    const labels = {
      admin: "Admin",
      titolare: "Titolare",
      collaboratore: "Collaboratore"
    };
    return (
      <Badge variant="outline" className={`${styles[role]} border text-xs font-medium`}>
        {labels[role]}
      </Badge>
    );
  };
  
  const totalCredits = allWallets.reduce((sum, w) => sum + (w.balance || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/30" data-testid="wallet-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50 backdrop-blur-xl bg-white/80">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Portafoglio Ortho
                </h1>
                <p className="text-xs text-slate-500 flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-amber-500" />
                  1 Ortho = 1 Euro
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {canManageWallet && (
                <>
                  <div className="hidden sm:flex bg-slate-100 rounded-full p-1">
                    <Button
                      variant={viewMode === "personal" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("personal")}
                      className={`rounded-full ${viewMode === "personal" ? "bg-white shadow-sm" : ""}`}
                    >
                      <User className="w-4 h-4 mr-1" />
                      Mio
                    </Button>
                    <Button
                      variant={viewMode === "all" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("all")}
                      className={`rounded-full ${viewMode === "all" ? "bg-white shadow-sm" : ""}`}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Tutti
                    </Button>
                  </div>
                  
                  <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-full shadow-lg shadow-emerald-500/25" data-testid="add-credits-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Ricarica
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                            <Coins className="w-4 h-4 text-white" />
                          </div>
                          Ricarica Ortho
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label htmlFor="user-select">Collaboratore *</Label>
                          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                            <SelectTrigger className="mt-1.5" data-testid="user-select">
                              <SelectValue placeholder="Seleziona collaboratore" />
                            </SelectTrigger>
                            <SelectContent>
                              {allWallets.map((w) => (
                                <SelectItem key={w.user_id} value={w.user_id}>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{w.name}</span>
                                    {getRoleBadge(w.role)}
                                    <span className="text-slate-400 text-sm">({w.balance.toFixed(0)} Ortho)</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div>
                          <Label htmlFor="amount">Importo (Ortho) *</Label>
                          <div className="relative mt-1.5">
                            <Input
                              id="amount"
                              type="number"
                              min="1"
                              step="0.01"
                              value={amount}
                              onChange={(e) => setAmount(e.target.value)}
                              placeholder="100"
                              className="pl-10"
                              data-testid="amount-input"
                            />
                            <Coins className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          </div>
                        </div>
                        
                        <div>
                          <Label htmlFor="description">Descrizione</Label>
                          <Input
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ricarica mensile"
                            className="mt-1.5"
                            data-testid="description-input"
                          />
                        </div>
                        
                        <div className="flex justify-end gap-3 pt-4">
                          <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-full">
                            Annulla
                          </Button>
                          <Button
                            onClick={handleAddCredits}
                            disabled={processing}
                            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-full"
                            data-testid="confirm-add-btn"
                          >
                            {processing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Elaborazione...
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-2" />
                                Aggiungi
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Personal Balance Card - Hero Style */}
        <div className="relative overflow-hidden rounded-3xl mb-8">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800">
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }} />
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-amber-500/20 to-orange-500/20 rounded-full blur-3xl" />
          
          <div className="relative p-8 sm:p-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <Coins className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Il Mio Saldo</p>
                    <p className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="balance">
                      {loading ? (
                        <Skeleton className="h-12 w-40 bg-white/20" />
                      ) : (
                        <span className="flex items-baseline gap-2">
                          {wallet?.balance?.toFixed(2) || "0.00"}
                          <span className="text-xl text-slate-400">Ortho</span>
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <p className="text-slate-400 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  ≈ €{loading ? "..." : (wallet?.balance?.toFixed(2) || "0.00")}
                </p>
              </div>
              
              {canManageWallet && !loading && (
                <div className="flex flex-col gap-2 text-right">
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                    <p className="text-xs text-slate-400">Totale Circolante</p>
                    <p className="text-2xl font-bold text-emerald-400">{totalCredits.toFixed(0)} Ortho</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Mobile View Toggle */}
        {canManageWallet && (
          <div className="sm:hidden flex bg-slate-100 rounded-full p-1 mb-6">
            <Button
              variant={viewMode === "personal" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("personal")}
              className={`flex-1 rounded-full ${viewMode === "personal" ? "bg-white shadow-sm" : ""}`}
            >
              <User className="w-4 h-4 mr-1" />
              Mio
            </Button>
            <Button
              variant={viewMode === "all" ? "default" : "ghost"}
              size="sm"
              onClick={() => setViewMode("all")}
              className={`flex-1 rounded-full ${viewMode === "all" ? "bg-white shadow-sm" : ""}`}
            >
              <Users className="w-4 h-4 mr-1" />
              Tutti
            </Button>
          </div>
        )}
        
        {/* All Collaborators Wallets */}
        {canManageWallet && viewMode === "all" && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden mb-6">
            <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-600" />
                Saldi Collaboratori
              </h2>
            </div>
            
            {loading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                ))}
              </div>
            ) : allWallets.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nessun collaboratore</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {allWallets.map((w, index) => (
                  <div
                    key={w.user_id}
                    className="p-5 hover:bg-slate-50/50 transition-all flex items-center gap-4 animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`wallet-${w.user_id}`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      w.role === 'admin' ? 'bg-gradient-to-br from-purple-500 to-indigo-500' :
                      w.role === 'titolare' ? 'bg-gradient-to-br from-amber-500 to-orange-500' :
                      'bg-gradient-to-br from-slate-400 to-slate-500'
                    }`}>
                      <User className="w-6 h-6 text-white" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">{w.name}</span>
                        {getRoleBadge(w.role)}
                      </div>
                      <p className="text-sm text-slate-500">@{w.username}</p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xl font-bold text-slate-900">{w.balance.toFixed(2)}</p>
                        <p className="text-xs text-slate-500">Ortho</p>
                      </div>
                      
                      {w.balance > 0 && w.role === "collaboratore" && (
                        <Button
                          size="sm"
                          onClick={() => handleSettleWallet(w.user_id, w.name, w.balance)}
                          disabled={processing}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-full shadow-md"
                          data-testid={`settle-btn-${w.user_id}`}
                        >
                          {processing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Salda
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Personal Transactions */}
        {viewMode === "personal" && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Le Mie Transazioni
              </h2>
            </div>
            
            {loading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex items-center gap-4">
                    <Skeleton className="h-12 w-12 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !wallet?.transactions?.length ? (
              <div className="p-16 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wallet className="w-10 h-10 text-slate-400" />
                </div>
                <p className="text-slate-500 font-medium">Nessuna transazione</p>
                <p className="text-sm text-slate-400 mt-1">Le tue transazioni appariranno qui</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {[...wallet.transactions].reverse().map((tx, index) => (
                  <div
                    key={tx.tx_id}
                    className="p-5 hover:bg-slate-50/50 transition-all flex items-center gap-4 animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`tx-${tx.tx_id}`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                      tx.type === "credit" 
                        ? "bg-gradient-to-br from-emerald-400 to-green-500" 
                        : tx.type === "settlement"
                        ? "bg-gradient-to-br from-blue-400 to-indigo-500"
                        : "bg-gradient-to-br from-red-400 to-rose-500"
                    }`}>
                      {tx.type === "credit" ? (
                        <ArrowDownRight className="w-6 h-6 text-white" />
                      ) : tx.type === "settlement" ? (
                        <CheckCircle className="w-6 h-6 text-white" />
                      ) : (
                        <ArrowUpRight className="w-6 h-6 text-white" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900">{tx.description}</p>
                      <p className="text-sm text-slate-500">
                        {tx.created_by_name} • {formatDate(tx.created_at)}
                      </p>
                    </div>
                    
                    <div className={`text-right ${
                      tx.type === "credit" ? "text-emerald-600" : 
                      tx.type === "settlement" ? "text-blue-600" : "text-red-600"
                    }`}>
                      <p className="text-xl font-bold">
                        {tx.type === "credit" ? "+" : ""}{tx.amount?.toFixed(2)}
                      </p>
                      <p className="text-xs opacity-70">Ortho</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default WalletPage;
